/**
 * Hook useFlowContext - CIPA Fácil
 *
 * Centraliza a coleta de informações necessárias para validar
 * os gates de fluxo do sistema NR-05.
 *
 * Integra com:
 * - AuthContext (empresa selecionada, tipo de usuário)
 * - mandatoService (mandato ativo)
 * - membroService (total de membros)
 * - reuniaoService (reuniões existentes)
 *
 * @author CIPA Fácil Team
 * @date 2026-02-02
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { mandatoService, MandatoComNomes } from "../services/mandatoService";
import { membroService } from "../services/membroService";
import { listarReunioesPorMandato } from "../services/reuniaoService";
import { supabase } from "../config/supabase";
import {
  FlowContext,
  PlanoUsuario,
  StatusAssinatura,
  canAccess,
  canExport,
  getNextRecommendedStep,
  getBlockedSteps,
  getModulosHabilitados,
  FlowStep,
  GateResult,
} from "../utils/flowGates";

/**
 * Resultado do hook useFlowContext
 */
export interface UseFlowContextResult {
  /** Contexto completo do fluxo */
  context: FlowContext;

  /** Se está carregando dados */
  loading: boolean;

  /** Erro ao carregar (se houver) */
  error: string | null;

  /** Mandato ativo atual */
  mandatoAtivo: MandatoComNomes | null;

  /** Função para recarregar dados */
  refresh: () => Promise<void>;

  /** Verifica se pode acessar uma etapa específica */
  canAccessStep: (step: FlowStep) => GateResult;

  /** Verifica se pode exportar */
  canExport: boolean;

  /** Próxima etapa recomendada */
  nextStep: FlowStep | null;

  /** Lista de etapas bloqueadas */
  blockedSteps: FlowStep[];
}

/**
 * Mapeia tipo_usuario do banco (cp_parametros_perfil) para PlanoUsuario
 *
 * Banco atual:
 * - BASICO → STARTER (1 empresa, 50 eleitores)
 * - PROFISSIONAL → PROFESSIONAL (3 empresas, 200 eleitores)
 * - ENTERPRISE → ENTERPRISE (10 empresas, ilimitado)
 * - SUPER_ADMIN → SUPER_ADMIN (ilimitado)
 *
 * Roadmap (target):
 * - STARTER → 3 empresas
 * - PROFESSIONAL → 10 empresas
 * - ENTERPRISE → ilimitado
 */
function mapTipoUsuarioToPlano(tipoUsuario?: string): PlanoUsuario {
  // Sem tipo definido = STARTER (menor plano válido)
  if (!tipoUsuario) return "STARTER";

  const upperType = tipoUsuario.toUpperCase();

  const mapping: Record<string, PlanoUsuario> = {
    // Mapeamento do banco atual (cp_parametros_perfil)
    BASICO: "STARTER",
    PROFISSIONAL: "PROFESSIONAL",
    ENTERPRISE: "ENTERPRISE",
    SUPER_ADMIN: "SUPER_ADMIN",
    // Mapeamento direto (novos planos)
    STARTER: "STARTER",
    PROFESSIONAL: "PROFESSIONAL",
    // Mapeamento legado de roles
    ADMIN: "ENTERPRISE",
    GESTOR: "PROFESSIONAL",
    USUARIO: "STARTER",
  };

  // Default para STARTER (menor plano válido)
  return mapping[upperType] || "STARTER";
}

/**
 * Mapeia status_assinatura legado para StatusAssinatura
 * @internal Será usado quando integrarmos com cp_assinaturas
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function mapStatusAssinatura(status?: string): StatusAssinatura {
  if (!status) return "active";

  const mapping: Record<string, StatusAssinatura> = {
    ativo: "active",
    ativa: "active",
    active: "active",
    cancelado: "canceled",
    canceled: "canceled",
    suspenso: "suspended",
    suspended: "suspended",
    expirado: "expired",
    expired: "expired",
    pendente: "pending",
    pending: "pending",
  };

  return mapping[status.toLowerCase()] || "active";
}

/**
 * Hook que coleta e mantém o contexto necessário para validação dos gates de fluxo.
 *
 * @returns Contexto do fluxo com funções de validação
 *
 * @example
 * ```tsx
 * function MeuComponente() {
 *   const { context, canAccessStep, loading } = useFlowContext();
 *
 *   if (loading) return <Loading />;
 *
 *   const result = canAccessStep('cronograma');
 *   if (!result.allowed) {
 *     return <BlockedMessage {...result} />;
 *   }
 *
 *   return <CronogramaContent />;
 * }
 * ```
 */
export function useFlowContext(): UseFlowContextResult {
  const { user, empresaSelecionada } = useAuth();

  // Estado local
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mandatoAtivo, setMandatoAtivo] = useState<MandatoComNomes | null>(
    null,
  );
  const [totalMembros, setTotalMembros] = useState(0);
  const [temReunioes, setTemReunioes] = useState(false);
  const [temAtas, setTemAtas] = useState(false);
  const [planoUsuario, setPlanoUsuario] = useState<PlanoUsuario>("STARTER");
  const [statusAssinatura, setStatusAssinatura] =
    useState<StatusAssinatura>("active");

  /**
   * Carrega dados do contexto
   */
  const loadContext = useCallback(async () => {
    if (!user?.id) {
      setMandatoAtivo(null);
      setTotalMembros(0);
      setTemReunioes(false);
      setTemAtas(false);
      setPlanoUsuario("STARTER");
      setStatusAssinatura("active");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Buscar assinatura ativa do usuário
      const { data: assinatura, error: assinaturaError } = await supabase
        .from("cp_assinaturas")
        .select("plano_atual, status_assinatura")
        .eq("usuario_id", user.id)
        .eq("status_assinatura", "active")
        .single();

      if (assinaturaError && assinaturaError.code !== "PGRST116") {
        throw assinaturaError;
      }

      // Usar plano da assinatura ou fallback para tipo_usuario do perfil (via mapTipoUsuarioToPlano).
      // Evita forçar STARTER para usuários ENTERPRISE que ainda não têm linha em cp_assinaturas.
      const plano =
        (assinatura?.plano_atual as PlanoUsuario) ||
        mapTipoUsuarioToPlano(user?.tipo_usuario);
      const status =
        (assinatura?.status_assinatura as StatusAssinatura) || "active";

      setPlanoUsuario(plano);
      setStatusAssinatura(status);

      if (!empresaSelecionada?.id) {
        setMandatoAtivo(null);
        setTotalMembros(0);
        setTemReunioes(false);
        setTemAtas(false);
        setLoading(false);
        return;
      }
      // 1. Buscar mandato ativo
      const mandato = await mandatoService.obterAtivo(empresaSelecionada.id);
      setMandatoAtivo(mandato);

      if (mandato?.id) {
        // 2. Buscar membros do mandato
        const membros = await membroService.listarPorMandato(mandato.id);
        setTotalMembros(membros.length);

        // 3. Buscar reuniões do mandato
        const reunioes = await listarReunioesPorMandato(mandato.id);
        setTemReunioes(reunioes.length > 0);

        // 4. Verificar se há atas (reuniões com status 'realizada' ou que já têm ata vinculada)
        const reunioesComAta = reunioes.filter(
          (r) => r.status === "realizada" || r.ata_id,
        );
        setTemAtas(reunioesComAta.length > 0);
      } else {
        setTotalMembros(0);
        setTemReunioes(false);
        setTemAtas(false);
      }
    } catch (err) {
      console.error("[useFlowContext] Erro ao carregar contexto:", err);
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setLoading(false);
    }
  }, [empresaSelecionada?.id]);

  // Carregar ao montar e quando empresa ou usuário mudar
  useEffect(() => {
    loadContext();
  }, [loadContext]);

  // Montar contexto do fluxo
  const context: FlowContext = useMemo(
    () => ({
      empresaSelecionada: empresaSelecionada?.id,
      temMandatoAtivo: mandatoAtivo !== null,
      mandatoAtivoId: mandatoAtivo?.id,
      totalMembros,
      planoUsuario,
      statusAssinatura,
      tipoUsuario: user?.tipo_usuario,
      temReunioes,
      temAtas,
      modulosHabilitados: getModulosHabilitados(planoUsuario),
      tipoComissao: empresaSelecionada?.tipo_comissao as
        | "CIPA"
        | "CIPATR"
        | "CIPAMIN"
        | undefined,
    }),
    [
      empresaSelecionada,
      mandatoAtivo,
      totalMembros,
      planoUsuario,
      statusAssinatura,
      user?.tipo_usuario,
      temReunioes,
      temAtas,
    ],
  );

  // Funções de validação memoizadas
  const canAccessStep = useCallback(
    (step: FlowStep): GateResult => {
      return canAccess(step, context);
    },
    [context],
  );

  const canExportDocs = useMemo(() => canExport(context), [context]);
  const nextStep = useMemo(() => getNextRecommendedStep(context), [context]);
  const blockedSteps = useMemo(() => getBlockedSteps(context), [context]);

  return {
    context,
    loading,
    error,
    mandatoAtivo,
    refresh: loadContext,
    canAccessStep,
    canExport: canExportDocs,
    nextStep,
    blockedSteps,
  };
}

export default useFlowContext;
